import React, { useRef, useEffect, useState } from 'react';
import { motion, useAnimation, useSpring, useTransform } from 'framer-motion';
import { Testimonial } from '../data/testimonials';

interface TestimonialRowProps {
  testimonials: Testimonial[];
  velocity: number;
  direction?: 'left' | 'right';
}

interface TestimonialCarouselProps {
  testimonialGroups: Testimonial[][];
  velocities?: number[];
}

const TestimonialCard: React.FC<Testimonial> = ({ content, author, title }) => (
  <div className="testimonial-card">
    <p className="testimonial-content">
      {content}
    </p>
    <div className="testimonial-author">
      <strong>{author}</strong>
      <span>{title}</span>
    </div>
  </div>
);

// Custom component that displays a row of testimonials and scrolls them
const TestimonialRow: React.FC<TestimonialRowProps> = ({ 
  testimonials, 
  velocity, 
  direction = 'right'
}) => {
  const actualVelocity = direction === 'left' ? -Math.abs(velocity) : Math.abs(velocity);
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const isMountedRef = useRef(false);
  
  // Get the container width for smooth animation
  useEffect(() => {
    if (!containerRef.current) return;
    
    const updateWidth = () => {
      if (containerRef.current) {
        // Calculating individual section width (full width / 3)
        const containerWidth = containerRef.current.scrollWidth / 3;
        setWidth(containerWidth);
      }
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [testimonials]);
  
  // Create the animation values
  const x = useRef(0);
  const controls = useAnimation();
  
  // Set the mounted state using ref
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  // Handle animation
  useEffect(() => {
    let animationFrameId: number | null = null;
    let isActive = true;
    
    if (width === 0 || !isMountedRef.current) return;
    
    const animate = async () => {
      x.current = 0;
      
      // Smooth infinite animation
      while (isActive && isMountedRef.current) {
        const targetX = x.current - width;
        
        // Check isMountedRef before starting animation
        if (!isMountedRef.current) break;
        
        await controls.start({
          x: targetX,
          transition: { 
            duration: Math.abs(width / actualVelocity), 
            ease: "linear" 
          }
        });
        
        // Check again after animation finishes
        if (!isActive || !isMountedRef.current) break;
        
        x.current = targetX;
        
        // When we've scrolled one full section, jump back without animation
        // Only reset if still mounted and the width is valid
        if (Math.abs(x.current) >= width && isActive && isMountedRef.current) {
          x.current = 0;
          controls.set({ x: 0 });
        }
      }
    };
    
    animate();
    
    // Clean up animation when component unmounts
    return () => {
      isActive = false;
      controls.stop();
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [width, actualVelocity, controls]);
  
  // Create three copies of the testimonials to ensure continuous scrolling
  const allCards = [...testimonials, ...testimonials, ...testimonials];
  
  return (
    <div className="testimonial-parallax">
      <div className="testimonial-outer-container" style={{ overflow: 'hidden' }}>
        <motion.div 
          className="testimonial-scroller"
          ref={containerRef}
          animate={controls}
          initial={{ x: 0 }}
        >
          {allCards.map((testimonial, index) => (
            <TestimonialCard
              key={`${testimonial.author}-${index}`}
              content={testimonial.content}
              author={testimonial.author}
              title={testimonial.title}
            />
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export const TestimonialCarousel: React.FC<TestimonialCarouselProps> = ({ 
  testimonialGroups,
  velocities = [25, 30]
}) => {
  // Alternate directions for each row
  const directions: ('left' | 'right')[] = ['right', 'left'];
  
  return (
    <div className="testimonials-wrapper">
      <div className="testimonial-cards">
        {testimonialGroups.map((group, index) => (
          <TestimonialRow
            key={index}
            testimonials={group}
            velocity={velocities[index % velocities.length]}
            direction={directions[index % directions.length]}
          />
        ))}
      </div>
    </div>
  );
};

export default TestimonialCarousel; 